import { useState } from 'react';

interface SignInWithEmailButtonProps {
  wasPlaying?: boolean;
  returnTo?: string;
  buttonStyle?: React.CSSProperties;
}

const SignInWithEmailButton = ({
  wasPlaying,
  returnTo: returnToProp,
  buttonStyle,
}: SignInWithEmailButtonProps) => {
  const [showInput, setShowInput] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    const returnTo =
      returnToProp ?? window.location.pathname + window.location.search;

    try {
      const response = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, returnTo, wasPlaying }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || 'Check your email for a sign-in link!');
        setEmail('');
      } else {
        setError(data.error || 'Failed to send sign-in link');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!showInput) {
    return (
      <button
        onClick={() => setShowInput(true)}
        className={buttonStyle ? undefined : 'pill-button'}
        style={
          buttonStyle ?? {
            backgroundColor: 'transparent',
            border: '1px solid var(--gold)',
            color: 'var(--gold)',
          }
        }
      >
        Sign in with Email
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          disabled={loading}
          style={{
            flex: 1,
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: '1px solid var(--gold)',
            backgroundColor: 'var(--bg)',
            color: 'var(--fg)',
            fontSize: '14px',
          }}
        />
        <button
          type="submit"
          disabled={loading || !email}
          className="pill-button"
          style={{ opacity: loading || !email ? 0.5 : 1 }}
        >
          {loading ? '...' : 'Send'}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowInput(false);
            setEmail('');
            setError(null);
            setMessage(null);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--muted)',
            cursor: 'pointer',
            padding: '0.5rem',
          }}
        >
          ✕
        </button>
      </div>
      {message && (
        <div style={{ color: 'var(--gold)', fontSize: '14px' }}>{message}</div>
      )}
      {error && (
        <div style={{ color: '#ff6b6b', fontSize: '14px' }}>{error}</div>
      )}
    </form>
  );
};

export default SignInWithEmailButton;
